/**
 * "Your app is the mobile one" — the store links and the trainer's way out.
 *
 * Two screens end here and they are reached from opposite ends: the sign-up role step, where the
 * visitor said they are a client before any account exists, and `pages/refused`, where the backend
 * said so about an account that already does. Everything below the heading is identical on both, so
 * it lives here rather than in one of them; the heading, the hero copy and the trailing action
 * differ, so those stay with each screen.
 *
 * **The store row renders only when a store URL exists** (`shared/config/mobileApp.ts`). A button
 * pointing at a listing that does not exist would fail in front of the one visitor this screen is for.
 */

import { useTranslation } from 'react-i18next';

import { MOBILE_APP_STORES } from '~/shared/config/mobileApp';

import styles from './MobileAppPointer.module.css';

interface MobileAppPointerProps {
  /** The quieter line under the links — each screen words its own. */
  hint: string;
}

export function MobileAppPointer({ hint }: MobileAppPointerProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.pointer}>
      {MOBILE_APP_STORES.length === 0 ? null : (
        <div className={styles.stores}>
          {MOBILE_APP_STORES.map((store) => (
            <a
              key={store.id}
              className={styles.store}
              href={store.url}
              // The store is another origin; `noreferrer` implies `noopener`, and both are named
              // because the pair is what stops the opened tab reaching back through `window.opener`.
              target="_blank"
              rel="noreferrer noopener"
            >
              {t(store.labelKey)}
            </a>
          ))}
        </div>
      )}
      <p className={styles.hint}>{hint}</p>
    </div>
  );
}
